import React, { useMemo, useState } from 'react'
import {
    Box,
    CircularProgress,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import AccountTreeIcon from '@material-ui/icons/AccountTree'
import FolderOpenIcon from '@material-ui/icons/FolderOpen'
import BusinessIcon from '@material-ui/icons/Business'
import AddIcon from '@material-ui/icons/Add'
import HowToRegIcon from '@material-ui/icons/HowToReg'
import FlashOnIcon from '@material-ui/icons/FlashOn'
import LibraryBooksIcon from '@material-ui/icons/LibraryBooks'
import TouchAppOutlinedIcon from '@material-ui/icons/TouchAppOutlined'
import { toast } from 'react-toastify'

import { useUser } from '../../hooks/useUser'
import {
    useCreatePropertyPipelineConfig,
    usePipelineConfigTemplates,
    usePropertyPipelineConfigs,
} from '../LeasingSeason/Configurations/PipelineBuilder/usePipelineConfigApi'
import {
    formatOrgPipelineProvenance,
    formatPipelineConfigProvenance,
} from '../LeasingSeason/Configurations/PipelineBuilder/pipelineConfigProvenance'
import {
    useApplyOrgLeadsPipelineToProperties,
    useOrgLeadsPipelineConfigs,
} from '../EzFolioConfigurations/useOrgPipelineConfigApi'
import { ConfigOptionCard } from './ConfigOptionCard'
import { NameConfigDialog } from './NameConfigDialog'
import type { ScreenPlacement } from './LeadsPipelineTimeline'
import {
    LEASING_SEASON_CONFIG_CONTENT_MAX_WIDTH,
    formatModifiedLabel,
} from './setupArtifacts'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        width: '100%',
        height: '100%',
        minHeight: 0,
        boxSizing: 'border-box',
        padding: theme.spacing(0.5, 1, 1),
        display: 'flex',
        justifyContent: 'center',
        overflow: 'hidden',
    },
    panel: {
        width: '100%',
        maxWidth: LEASING_SEASON_CONFIG_CONTENT_MAX_WIDTH,
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid #D5DBE3',
        borderRadius: 12,
        backgroundColor: theme.palette.background.paper,
        overflow: 'hidden',
        boxSizing: 'border-box',
    },
    panelHeader: {
        flexShrink: 0,
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(1.25),
        padding: theme.spacing(1.25, 1.5),
        borderBottom: '1px solid #E8ECF1',
        backgroundColor: theme.palette.grey[50],
    },
    headerIcon: {
        flexShrink: 0,
        width: 28,
        height: 28,
        borderRadius: 8,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#ECEFF1',
        color: '#455A64',
    },
    headerTitle: {
        fontSize: '0.8125rem',
        fontWeight: 700,
        lineHeight: 1.35,
    },
    headerDescription: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        lineHeight: 1.45,
        marginTop: 2,
    },
    grid: {
        flex: 1,
        minHeight: 0,
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gridTemplateRows: 'repeat(2, minmax(0, 1fr))',
        gap: theme.spacing(1),
        padding: theme.spacing(1),
        [theme.breakpoints.down('xs')]: {
            gridTemplateColumns: '1fr',
            gridTemplateRows: 'repeat(4, minmax(140px, 1fr))',
            overflow: 'auto',
        },
    },
    section: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        minWidth: 0,
        height: '100%',
        border: '1px solid #D5DBE3',
        borderRadius: 10,
        backgroundColor: theme.palette.background.paper,
        overflow: 'hidden',
        borderLeftWidth: 4,
    },
    sectionHeader: {
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        padding: theme.spacing(1, 1.25),
        borderBottom: '1px solid #E8ECF1',
        backgroundColor: theme.palette.grey[50],
    },
    sectionIcon: {
        width: 28,
        height: 28,
        borderRadius: 8,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    sectionTitle: {
        fontWeight: 700,
        fontSize: '0.75rem',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        color: theme.palette.text.primary,
    },
    sectionCount: {
        marginLeft: 'auto',
        fontSize: '0.75rem',
        fontWeight: 600,
        color: theme.palette.text.secondary,
        backgroundColor: theme.palette.common.white,
        border: '1px solid #D5DBE3',
        borderRadius: 999,
        padding: theme.spacing(0.25, 1),
    },
    sectionBody: {
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        padding: theme.spacing(1.25),
    },
    cardGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: theme.spacing(1.25),
    },
    loading: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
    },
    emptySection: {
        color: theme.palette.text.secondary,
        fontSize: '0.8125rem',
        padding: theme.spacing(1.5, 1),
        border: '1px dashed #C8CED6',
        borderRadius: 10,
        backgroundColor: theme.palette.grey[50],
        textAlign: 'center',
    },
}))

type PendingCreate =
    | { kind: 'ezos'; templateKey: string; defaultName: string }
    | { kind: 'org'; orgConfigId: number; defaultName: string }
    | { kind: 'scratch'; defaultName: string }

type SectionTone = {
    accent: string
    iconBg: string
    iconColor: string
}

const SECTION_TONES: Record<string, SectionTone> = {
    existing: {
        accent: '#2E7D32',
        iconBg: '#E8F5E9',
        iconColor: '#2E7D32',
    },
    ezos: {
        accent: '#1565C0',
        iconBg: '#E3F2FD',
        iconColor: '#1565C0',
    },
    org: {
        accent: '#455A64',
        iconBg: '#ECEFF1',
        iconColor: '#455A64',
    },
    scratch: {
        accent: '#9E9E9E',
        iconBg: '#F5F5F5',
        iconColor: '#616161',
    },
}

const EZOS_TEMPLATE_KEYS = new Set(['qualify_first', 'sign_fast'])

const TEMPLATE_SHORT: Record<
    string,
    { blurb: string; icon: React.ReactNode; iconBg: string; iconColor: string }
> = {
    qualify_first: {
        blurb: 'Application → Screening → Qualification → Lease',
        icon: <HowToRegIcon fontSize="small" />,
        iconBg: '#E8F5E9',
        iconColor: '#2E7D32',
    },
    sign_fast: {
        blurb: 'Application → Lease → Screening → Qualification',
        icon: <FlashOnIcon fontSize="small" />,
        iconBg: '#FFF3E0',
        iconColor: '#EF6C00',
    },
}

function templatePresentation(
    key: string,
    fallbackDescription?: string | null,
) {
    return (
        TEMPLATE_SHORT[key] ?? {
            blurb: fallbackDescription?.split('.')[0] || 'Custom template',
            icon: <AccountTreeIcon fontSize="small" />,
            iconBg: '#E3F2FD',
            iconColor: '#1565C0',
        }
    )
}

/** Pipeline pick / create UI — same four lanes as Setup artifact choosers. */
export const LeadsPipelineChooser: React.FC<{
    onConfigSelected: (
        configId: number,
        scratchPlacement?: ScreenPlacement,
    ) => void
}> = ({ onConfigSelected }) => {
    const classes = useStyles()
    const { workspaceUser, organization } = useUser()
    const apartmentId = workspaceUser?.active_workspace?.id

    const configsQuery = usePropertyPipelineConfigs(apartmentId)
    const templatesQuery = usePipelineConfigTemplates()
    const orgConfigsQuery = useOrgLeadsPipelineConfigs(organization)
    const createMutation = useCreatePropertyPipelineConfig(apartmentId)
    const applyOrgMutation = useApplyOrgLeadsPipelineToProperties(organization)

    const [pending, setPending] = useState<PendingCreate | null>(null)

    const configs = configsQuery.data ?? []
    const templates = (templatesQuery.data ?? []).filter((template) =>
        EZOS_TEMPLATE_KEYS.has(template.key),
    )
    const orgConfigs = orgConfigsQuery.data ?? []

    const templatesByKey = useMemo(
        () => new Map(templates.map((template) => [template.key, template])),
        [templates],
    )

    const submitting = createMutation.isLoading || applyOrgMutation.isLoading

    const closeDialog = () => {
        if (!submitting) {
            setPending(null)
        }
    }

    const handleConfirmName = async (
        name: string,
        screenPlacement?: ScreenPlacement,
    ) => {
        if (!pending || apartmentId == null) return

        if (pending.kind === 'ezos') {
            createMutation.mutate(
                { name, template_key: pending.templateKey },
                {
                    onSuccess: (data) => {
                        setPending(null)
                        onConfigSelected(data.id)
                    },
                    onError: () => {
                        toast.error('Could not create pipeline configuration.')
                    },
                },
            )
            return
        }

        if (pending.kind === 'scratch') {
            createMutation.mutate(
                { name },
                {
                    onSuccess: (data) => {
                        setPending(null)
                        onConfigSelected(data.id, screenPlacement)
                    },
                    onError: () => {
                        toast.error('Could not create pipeline configuration.')
                    },
                },
            )
            return
        }

        try {
            const result = await applyOrgMutation.mutateAsync({
                configId: pending.orgConfigId,
                property_ids: [apartmentId],
                name,
            })

            if (
                result.skipped_locked > 0 &&
                result.created === 0 &&
                result.updated === 0
            ) {
                toast.error(
                    'An existing config from this org template is locked and could not be updated.',
                )
                return
            }

            const refreshed = await configsQuery.refetch()
            const match = (refreshed.data ?? []).find(
                (row) =>
                    row.created_from_org_pipeline_id === pending.orgConfigId,
            )

            if (!match) {
                toast.error(
                    'Org template was applied, but the property config could not be found. Refresh and try Edit existing.',
                )
                setPending(null)
                return
            }

            setPending(null)
            onConfigSelected(match.id)
        } catch {
            toast.error('Could not apply org pipeline template.')
        }
    }

    if (apartmentId == null) {
        return (
            <Box className={classes.root}>
                <Typography color="textSecondary">
                    Select a property workspace to manage leasing season
                    configuration.
                </Typography>
            </Box>
        )
    }

    if (configsQuery.isLoading || templatesQuery.isLoading) {
        return (
            <Box className={classes.loading}>
                <CircularProgress size={28} />
            </Box>
        )
    }

    const dialogTitle =
        pending?.kind === 'ezos'
            ? 'Name your configuration'
            : pending?.kind === 'org'
            ? 'Name configuration from org template'
            : 'Name your configuration'

    const confirmLabel =
        pending?.kind === 'org' ? 'Apply & open' : 'Create & open'

    const renderSection = ({
        toneKey,
        title,
        icon,
        count,
        children,
    }: {
        toneKey: keyof typeof SECTION_TONES
        title: string
        icon: React.ReactNode
        count?: number
        children: React.ReactNode
    }) => {
        const tone = SECTION_TONES[toneKey]
        return (
            <Box
                className={classes.section}
                style={{ borderLeftColor: tone.accent }}
            >
                <Box className={classes.sectionHeader}>
                    <Box
                        className={classes.sectionIcon}
                        style={{
                            backgroundColor: tone.iconBg,
                            color: tone.iconColor,
                        }}
                    >
                        {icon}
                    </Box>
                    <Typography className={classes.sectionTitle}>
                        {title}
                    </Typography>
                    {count != null ? (
                        <Typography className={classes.sectionCount}>
                            {count}
                        </Typography>
                    ) : null}
                </Box>
                <Box className={classes.sectionBody}>{children}</Box>
            </Box>
        )
    }

    const editExistingSection = renderSection({
        toneKey: 'existing',
        title: 'Edit existing',
        icon: <FolderOpenIcon style={{ fontSize: 18 }} />,
        count: configs.length,
        children:
            configs.length === 0 ? (
                <Typography className={classes.emptySection}>
                    No property pipeline configurations yet.
                </Typography>
            ) : (
                <Box className={classes.cardGrid}>
                    {configs.map((config) => (
                        <ConfigOptionCard
                            key={config.id}
                            title={config.name}
                            description={
                                config.is_editable
                                    ? formatPipelineConfigProvenance(
                                          config,
                                          templatesByKey,
                                      ) ?? 'Owned by this property'
                                    : 'Locked (linked to a season)'
                            }
                            meta={formatModifiedLabel(config.modified_date)}
                            icon={<FolderOpenIcon fontSize="small" />}
                            iconBackground={SECTION_TONES.existing.iconBg}
                            iconColor={SECTION_TONES.existing.iconColor}
                            onClick={() => onConfigSelected(config.id)}
                        />
                    ))}
                </Box>
            ),
    })

    const ezosSection = renderSection({
        toneKey: 'ezos',
        title: 'Build from EZOS',
        icon: <LibraryBooksIcon style={{ fontSize: 18 }} />,
        count: templates.length,
        children:
            templates.length === 0 ? (
                <Typography className={classes.emptySection}>
                    No EZOS templates available.
                </Typography>
            ) : (
                <Box className={classes.cardGrid}>
                    {templates.map((template) => {
                        const presentation = templatePresentation(
                            template.key,
                            template.description,
                        )
                        return (
                            <ConfigOptionCard
                                key={template.key}
                                title={template.name}
                                description={presentation.blurb}
                                icon={presentation.icon}
                                iconBackground={presentation.iconBg}
                                iconColor={presentation.iconColor}
                                disabled={submitting}
                                onClick={() =>
                                    setPending({
                                        kind: 'ezos',
                                        templateKey: template.key,
                                        defaultName: template.name,
                                    })
                                }
                            />
                        )
                    })}
                </Box>
            ),
    })

    const orgSection = renderSection({
        toneKey: 'org',
        title: 'Build from org template',
        icon: <BusinessIcon style={{ fontSize: 18 }} />,
        count: orgConfigs.length,
        children:
            organization == null ? (
                <Typography className={classes.emptySection}>
                    No organization selected.
                </Typography>
            ) : orgConfigsQuery.isLoading ? (
                <Box className={classes.loading}>
                    <CircularProgress size={22} />
                </Box>
            ) : orgConfigs.length === 0 ? (
                <Typography className={classes.emptySection}>
                    No org templates yet — create one in EZFolio, or start from
                    EZOS.
                </Typography>
            ) : (
                <Box className={classes.cardGrid}>
                    {orgConfigs.map((orgConfig) => (
                        <ConfigOptionCard
                            key={orgConfig.id}
                            title={orgConfig.name}
                            description={
                                formatOrgPipelineProvenance(
                                    orgConfig,
                                    templatesByKey,
                                ) ??
                                'Org template · copy to this property (editable)'
                            }
                            meta={formatModifiedLabel(orgConfig.modified_date)}
                            icon={<BusinessIcon fontSize="small" />}
                            iconBackground={SECTION_TONES.org.iconBg}
                            iconColor={SECTION_TONES.org.iconColor}
                            disabled={submitting}
                            onClick={() =>
                                setPending({
                                    kind: 'org',
                                    orgConfigId: orgConfig.id,
                                    defaultName: orgConfig.name,
                                })
                            }
                        />
                    ))}
                </Box>
            ),
    })

    const scratchSection = renderSection({
        toneKey: 'scratch',
        title: 'Create your own',
        icon: <AddIcon style={{ fontSize: 18 }} />,
        children: (
            <Box className={classes.cardGrid}>
                <ConfigOptionCard
                    title="Build from scratch"
                    description="Blank pipeline — add every step yourself."
                    icon={<AddIcon fontSize="small" />}
                    iconBackground={SECTION_TONES.scratch.iconBg}
                    iconColor={SECTION_TONES.scratch.iconColor}
                    muted
                    disabled={submitting}
                    onClick={() =>
                        setPending({
                            kind: 'scratch',
                            defaultName: 'Custom pipeline',
                        })
                    }
                />
            </Box>
        ),
    })

    return (
        <Box className={classes.root}>
            <Box className={classes.panel}>
                <Box className={classes.panelHeader}>
                    <Box className={classes.headerIcon}>
                        <TouchAppOutlinedIcon style={{ fontSize: 18 }} />
                    </Box>
                    <Box>
                        <Typography className={classes.headerTitle}>
                            Start by choosing a template or existing config.
                        </Typography>
                        <Typography className={classes.headerDescription}>
                            Edit existing, build from EZOS or org, or create
                            your own. Templates set Screening placement; scratch
                            asks when you name the config.
                        </Typography>
                    </Box>
                </Box>
                <Box className={classes.grid}>
                    {editExistingSection}
                    {orgSection}
                    {ezosSection}
                    {scratchSection}
                </Box>
            </Box>

            <NameConfigDialog
                open={pending != null}
                title={dialogTitle}
                confirmLabel={confirmLabel}
                initialName={pending?.defaultName ?? ''}
                submitting={submitting}
                askScreenPlacement={pending?.kind === 'scratch'}
                onClose={closeDialog}
                onConfirm={handleConfirmName}
            />
        </Box>
    )
}
